import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, DEPARTMENTS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileArrowUp, Trash, DownloadSimple, File as FileIcon, FolderOpen, EnvelopeSimple, Eye } from "@phosphor-icons/react";
import DocViewer from "@/components/DocViewer";
import ActivityFlowActions from "@/components/ActivityFlowActions";
import { Checkbox } from "@/components/ui/checkbox";

const CATS = {
  contract: "Contract", oferta: "Ofertă", factura: "Factură", proforma: "Proformă",
  comanda: "Comandă", aviz: "Aviz", cmr: "CMR", desen: "Desen tehnic",
  fisa_proiect: "Fișă proiect", pv: "Proces-verbal", certificat: "Certificat",
  dovada: "Dovadă încasare", nota_santier: "Notă șantier", email_atasament: "Atașament email", altul: "Altul",
};

const CAT_CLS = "bg-white/5 text-muted-foreground";

// Tipuri comune (chei) — filtrate per departament prin /doc-common (ca în hub-ul de departament)
const COMMON_KEYS = ["contract", "oferta", "proforma", "factura", "comanda", "aviz", "cmr", "desen",
  "fisa_proiect", "pv", "certificat", "dovada", "nota_santier"];

const EMAIL_CATS = [
  ["oferta", "Ofertă"], ["comanda", "Comandă"], ["factura", "Factură"],
  ["reclamatie", "Reclamație"], ["tehnic", "Tehnic"], ["logistica", "Logistică"], ["altul", "Altul"],
];

export default function ProjectFolderPanel({ projectId, positions = [], readOnly = false, highlightDoc = null }) {
  const { user, hasRole } = useAuth();
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState("contract");
  const [position, setPosition] = useState("");
  const [uploading, setUploading] = useState(false);
  const [hlId, setHlId] = useState(null);
  const hlTimer = useRef(null);
  const [stages, setStages] = useState([]);
  const [uploadStage, setUploadStage] = useState("");
  const [filterStage, setFilterStage] = useState("__all");
  const [search, setSearch] = useState("");
  const [viewFile, setViewFile] = useState(null);
  const [docTypes, setDocTypes] = useState([]);
  const [common, setCommon] = useState(COMMON_KEYS);
  const [uploadingMulti, setUploadingMulti] = useState(false);
  const multiRef = useRef(null);
  const fileRef = useRef(null);
  const userDepts = (user?.departments && user.departments.length) ? user.departments : (user?.department ? [user.department] : []);
  const [nomDept, setNomDept] = useState(userDepts[0] || "");
  const deptOptions = hasRole("admin") ? DEPARTMENTS : DEPARTMENTS.filter((d) => userDepts.includes(d.key));
  const deptLabel = (DEPARTMENTS.find((d) => d.key === nomDept) || {}).label || "—";
  const canMulti = ["proiectare", "proiectare_structuri"].includes(nomDept) || hasRole("admin");
  const canUpload = !!user; // orice utilizator autentificat poate încărca în dosarul digital
  const canDelete = true;
  const [fsel, setFsel] = useState([]);
  const [dFrom, setDFrom] = useState("");
  const [dTo, setDTo] = useState("");
  const toggleFsel = (id) => setFsel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const load = () => api.get(`/projects/${projectId}/folder`).then((r) => setFiles(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  // Încarcă etapele fluxului pentru select/filtru/etichetă și presetează etapa curentă.
  useEffect(() => {
    api.get(`/projects/${projectId}/wf-next`).then((r) => {
      const s = r.data?.stages || [];
      setStages(s);
      const cur = s[r.data?.stage];
      if (cur) setUploadStage(cur.key);
    }).catch(() => {});
  }, [projectId]);

  // Evidențiază documentul specific etapei alese din linia de flux (mapare pe categorie + cuvinte-cheie din nume).
  useEffect(() => {
    const doc = highlightDoc && highlightDoc.doc;
    if (!doc || !files.length) return;
    const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const DOC_CAT = { "contract semnat": "contract", "factură": "factura", "confirmare încasare": "dovada", "fișă proiect": "fisa_proiect", "comenzi furnizori": "comanda" };
    const docN = norm(doc);
    const cat = DOC_CAT[docN];
    const kws = docN.split(/[^a-z0-9]+/).filter((w) => w.length >= 4);
    let match = (highlightDoc.key && files.find((f) => f.stage_key === highlightDoc.key)) || (cat ? files.find((f) => f.category === cat) : null);
    if (!match) match = files.find((f) => kws.some((k) => norm(f.name).includes(k) || norm(CATS[f.category] || f.category).includes(k)));
    if (match) {
      setHlId(match.id);
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="folder-file-${match.id}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 150);
      clearTimeout(hlTimer.current);
      hlTimer.current = setTimeout(() => setHlId(null), 4000);
    } else {
      toast.info(`Documentul „${doc}" nu a fost găsit în dosar — încarcă-l la această etapă.`);
    }
    // eslint-disable-next-line
  }, [highlightDoc, files]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      fd.append("name", file.name);
      fd.append("department", nomDept);
      if (position) fd.append("position", position);
      if (uploadStage) {
        const st = stages.find((x) => x.key === uploadStage);
        fd.append("stage_key", uploadStage);
        fd.append("stage_label", st ? st.doc : "");
      }
      await api.post(`/projects/${projectId}/folder`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document adăugat în dosarul digital");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const loadTypes = () => {
    if (nomDept) {
      api.get(`/doc-types?department=${nomDept}`).then((r) => setDocTypes(r.data || [])).catch(() => {});
      api.get(`/doc-common?department=${nomDept}`).then((r) => setCommon(r.data?.enabled || COMMON_KEYS)).catch(() => setCommon(COMMON_KEYS));
    } else { setDocTypes([]); setCommon(COMMON_KEYS); }
  };
  useEffect(() => { loadTypes(); /* eslint-disable-next-line */ }, [nomDept]);

  const onCatChange = async (v) => {
    if (v !== "__add") { setCategory(v); return; }
    if (!nomDept) { toast.error("Alege întâi departamentul nomenclatorului"); return; }
    const label = (window.prompt(`Denumește noul tip de document (se salvează în nomenclatorul departamentului ${deptLabel}):`) || "").trim();
    if (!label) return;
    try {
      await api.post("/doc-types", { department: nomDept, label });
      loadTypes();
      setCategory(label);
      toast.success(`Tip de document adăugat la ${deptLabel}: ${label}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const onPickMulti = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploadingMulti(true);
    try {
      const fd = new FormData();
      list.forEach((f) => fd.append("files", f));
      fd.append("category", category);
      fd.append("department", nomDept);
      if (position) fd.append("position", position);
      if (uploadStage) { const st = stages.find((x) => x.key === uploadStage); fd.append("stage_key", uploadStage); fd.append("stage_label", st ? st.doc : ""); }
      const { data } = await api.post(`/projects/${projectId}/folder/multi`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.saved} documente adăugate în dosar`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploadingMulti(false); if (multiRef.current) multiRef.current.value = ""; }
  };

  const del = async (fid) => { await api.delete(`/projects/${projectId}/folder/${fid}`); load(); };

  const canReclass = hasRole("pm", "inginer", "admin");
  const reclassify = async (fid, cat) => {
    try {
      await api.put(`/inbox/emails/${fid}/category`, { category: cat });
      setFiles((arr) => arr.map((f) => (f.id === fid ? { ...f, email_category: cat } : f)));
      toast.success("Categorie corectată");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // Grupare pe 2 niveluri: Rezervor (poziție) → Utilizator (cine a încărcat) → documente
  const sq = search.trim().toLowerCase();
  const shownFiles = (filterStage === "__all" ? files : files.filter((f) => f.stage_key === filterStage))
    .filter((f) => !sq || (f.name || "").toLowerCase().includes(sq)
      || (CATS[f.category] || f.category || "").toLowerCase().includes(sq)
      || (f.uploaded_by_name || "").toLowerCase().includes(sq)
      || (f.uploaded_by_email || "").toLowerCase().includes(sq))
    .filter((f) => !dFrom || !f.created_at || f.created_at.slice(0, 10) >= dFrom)
    .filter((f) => !dTo || !f.created_at || f.created_at.slice(0, 10) <= dTo);
  const posGroups = {};
  for (const f of shownFiles) {
    const posKey = f.folder === "EMAIL-URI PROIECT" ? "EMAIL-URI PROIECT" : (f.position ? `Rezervor ${f.position}` : "Comun (fără rezervor)");
    const userKey = f.uploaded_by_name || f.uploaded_by_email || "—";
    posGroups[posKey] = posGroups[posKey] || {};
    (posGroups[posKey][userKey] = posGroups[posKey][userKey] || []).push(f);
  }
  const posNames = Object.keys(posGroups).sort((a, b) => {
    if (a === "EMAIL-URI PROIECT") return 1;
    if (b === "EMAIL-URI PROIECT") return -1;
    if (a.startsWith("Comun")) return 1;
    if (b.startsWith("Comun")) return -1;
    return a.localeCompare(b);
  });

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="folder-panel">
      <div className="mb-4 flex items-center justify-end">
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/folder/export.zip?auth=${localStorage.getItem("hgs_token")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-emerald-400 transition-colors hover:border-emerald-400/50" title="Descarcă tot dosarul ca ZIP (Rezervor/Utilizator)" data-testid="folder-export-zip">
          <DownloadSimple size={15} /> Export dosar (ZIP)
        </a>
      </div>
      {canUpload && !readOnly && (
        <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-border pb-4">
          {deptOptions.length > 1 ? (
            <Select value={nomDept} onValueChange={setNomDept}>
              <SelectTrigger className="w-44" data-testid="folder-nomdept"><SelectValue placeholder="Departament nomenclator" /></SelectTrigger>
              <SelectContent>
                {deptOptions.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <span className="rounded-lg border border-border px-3 py-1.5 text-xs text-muted-foreground" data-testid="folder-nomdept-label">Nomenclator: {deptLabel}</span>
          )}
          <Select value={category} onValueChange={onCatChange}>
            <SelectTrigger className="w-48" data-testid="folder-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {common.map((k) => <SelectItem key={k} value={k}>{CATS[k] || k}</SelectItem>)}
              {docTypes.map((t) => <SelectItem key={t.id} value={t.label}>{t.label}</SelectItem>)}
              <SelectItem value="__add">➕ Altul (adaugă tip nou)…</SelectItem>
            </SelectContent>
          </Select>
          {positions.length > 0 && (
            <Select value={position || "__none"} onValueChange={(v) => setPosition(v === "__none" ? "" : v)}>
              <SelectTrigger className="w-52" data-testid="folder-position"><SelectValue placeholder="Rezervor (opțional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Fără rezervor (comun)</SelectItem>
                {positions.map((p) => <SelectItem key={p.id} value={p.suffix || p.id}>Rezervor {p.suffix} {p.name ? `· ${p.name}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <input ref={fileRef} type="file" className="hidden" onChange={onPick} data-testid="folder-file-input" />
          {stages.length > 0 && (
            <Select value={uploadStage || "__none"} onValueChange={(v) => setUploadStage(v === "__none" ? "" : v)}>
              <SelectTrigger className="w-56" data-testid="folder-stage"><SelectValue placeholder="Etapă flux (opțional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Fără etapă</SelectItem>
                {stages.map((s, i) => <SelectItem key={s.key} value={s.key}>{i + 1}. {s.doc}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="folder-upload-btn">
            <FileArrowUp size={16} className="mr-1.5" /> {uploading ? "Se încarcă…" : "Adaugă document"}
          </Button>
          {canMulti && (
            <>
              <input ref={multiRef} type="file" multiple className="hidden" onChange={onPickMulti} data-testid="folder-multi-input" />
              <Button size="sm" variant="outline" onClick={() => multiRef.current?.click()} disabled={uploadingMulti} data-testid="folder-multi-btn">
                <FileArrowUp size={16} className="mr-1.5" /> {uploadingMulti ? "Se încarcă…" : "Mai multe deodată"}
              </Button>
            </>
          )}
          <span className="text-xs text-muted-foreground">Documentele proiectului sunt vizibile întregii echipe; emailurile personale rămân private.</span>
        </div>
      )}

      {!hasRole("admin") && (
        <p className="mb-3 rounded-lg border border-border bg-white/[0.02] px-3 py-2 text-xs text-muted-foreground" data-testid="folder-scope-note">
          Documentele proiectului (contract, ofertă, factură, PV, aviz etc.) sunt vizibile întregii echipe a proiectului. Doar emailurile din Smart Inbox rămân private (fiecare vede doar emailurile lui).
        </p>
      )}

      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-2" data-testid="folder-filters">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Caută în dosar (denumire, tip, utilizator)…" className="h-9 max-w-xs" data-testid="folder-search" />
          <Input type="date" value={dFrom} onChange={(e) => setDFrom(e.target.value)} className="h-9 w-36" title="De la data" data-testid="folder-date-from" />
          <Input type="date" value={dTo} onChange={(e) => setDTo(e.target.value)} className="h-9 w-36" title="Până la data" data-testid="folder-date-to" />
          {(dFrom || dTo || search) && <button onClick={() => { setSearch(""); setDFrom(""); setDTo(""); }} className="text-xs text-muted-foreground underline hover:text-foreground" data-testid="folder-filter-clear">Resetează</button>}
          {fsel.length > 0 && !readOnly && (
            <ActivityFlowActions projectId={projectId} projectCode="dosar" initialDocs={fsel} forwardOnly label={`Trimite mai departe (${fsel.length})`} onDone={() => setFsel([])} />
          )}
        </div>
        {stages.length > 0 && (
          <div className="flex items-center gap-2" data-testid="folder-filter-row">
            <span className="text-xs text-muted-foreground">Filtru etapă:</span>
            <Select value={filterStage} onValueChange={setFilterStage}>
              <SelectTrigger className="h-8 w-60" data-testid="folder-filter-stage"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Toate etapele</SelectItem>
                {stages.map((s, i) => <SelectItem key={s.key} value={s.key}>{i + 1}. {s.doc}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {posNames.map((posName) => {
          const userGroups = posGroups[posName];
          const userNames = Object.keys(userGroups).sort((a, b) => a.localeCompare(b));
          const totalCount = userNames.reduce((s, u) => s + userGroups[u].length, 0);
          const isEmail = posName === "EMAIL-URI PROIECT";
          return (
            <div key={posName} data-testid={`folder-group-${posName}`}>
              <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-muted-foreground">
                {isEmail ? <EnvelopeSimple size={16} className="text-amber-400" /> : <FolderOpen size={16} className="text-blue-400" />}
                {posName} <span className="text-muted-foreground/60">({totalCount})</span>
              </div>
              <div className="ml-2 space-y-4 border-l border-border pl-4">
                {userNames.map((uname) => (
                  <div key={uname} data-testid={`folder-user-${posName}::${uname}`}>
                    <div className="mb-1.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/20 text-[10px] font-bold text-primary">{(uname || "?").charAt(0).toUpperCase()}</span>
                      {uname} <span className="text-muted-foreground/50">({userGroups[uname].length})</span>
                    </div>
                    <div className="space-y-2">
                      {userGroups[uname].map((f) => (
                        <div key={f.id} className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-all ${hlId === f.id ? "border-primary bg-primary/[0.06] ring-2 ring-primary/60" : "border-border"}`} data-testid={`folder-file-${f.id}`}>
                          {!readOnly && <Checkbox checked={fsel.includes(f.id)} onCheckedChange={() => toggleFsel(f.id)} data-testid={`folder-sel-${f.id}`} />}
                          <FileIcon size={18} className="shrink-0 text-blue-400" />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium">{f.name}</div>
                            <div className="text-[11px] text-muted-foreground">{fmtDate(f.created_at)} · {f.uploaded_by_email || f.uploaded_by_name || "—"}</div>
                            {f.stage_label && <span className="mt-1 mr-1 inline-block rounded bg-blue-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-blue-300" data-testid={`file-stage-${f.id}`}>Etapă: {f.stage_label}</span>}
                            {f.email_category && f.email_category !== "altul" && <span className="mt-1 mr-1 inline-block rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-primary">{f.email_category}</span>}
                            {f.summary && <div className="mt-1 text-[11px] italic text-muted-foreground/90" data-testid={`file-summary-${f.id}`}>🧠 {f.summary}</div>}
                            {f.category === "email" && canReclass && (
                              <div className="mt-1.5 flex items-center gap-1.5">
                                <span className="text-[10px] text-muted-foreground">Categorie:</span>
                                <Select value={f.email_category || "altul"} onValueChange={(v) => reclassify(f.id, v)}>
                                  <SelectTrigger className="h-7 w-36 text-xs" data-testid={`reclass-${f.id}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>{EMAIL_CATS.map(([kk, ll]) => <SelectItem key={kk} value={kk}>{ll}</SelectItem>)}</SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                          <span className={`rounded px-1.5 py-0.5 text-[11px] ${CAT_CLS}`}>{CATS[f.category] || f.category}</span>
                          <button onClick={() => setViewFile(f)} className="text-muted-foreground hover:text-primary" title="Vizualizează în aplicație" data-testid={`folder-view-${f.id}`}><Eye size={17} /></button>
                          <a href={fileUrl(f.storage_path)} download={f.name} className="text-muted-foreground hover:text-foreground" data-testid={`folder-download-${f.id}`}><DownloadSimple size={17} /></a>
                          {canDelete && !readOnly && <button onClick={() => del(f.id)} className="text-muted-foreground hover:text-red-400" data-testid={`folder-del-${f.id}`}><Trash size={15} /></button>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {files.length === 0 && <p className="text-sm text-muted-foreground">Dosarul digital al proiectului este gol. Adaugă primul document.</p>}
        {files.length > 0 && shownFiles.length === 0 && <p className="text-sm text-muted-foreground" data-testid="folder-filter-empty">Niciun document pentru etapa selectată.</p>}
      </div>
      <DocViewer file={viewFile} onClose={() => setViewFile(null)} />
    </div>
  );
}
